import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ANALYTICS_KEYS } from "../../utills/analyticsKeys";
import posthog from "posthog-js";
import SimpleBar from "simplebar-react";
import CreditsModal from "../products/CreditsModal";
import CustomIcon from "./CustomIcon";
import AccountDropdown from "../user/AccountDropdown";
import UXAvatar from "./UXAvatar";
import WorkspaceSelection, { WorkspaceName } from "../organization/WorkspaceSelection";
import CreateStudyMenu from "../projects/detail/CreateStudyMenu";
import { PATHS } from "../../utills/paths";
import { PARTICIPANT_HUB_NAME } from "../../actions/constants";
import { useAuthContext } from "../user/AuthProvider";
import { Button, Divider, Flex, Layout, Popover, Tooltip, Typography } from "antd";

const { Sider } = Layout;
const { Text } = Typography;
const GLOBAL_SIDEBAR_MINIMIZED_KEY = "uxarmy-global-sidebar-minimized";

export function GlobalSidebarContent({ onNavigate = () => {}, onCollapse = null }) {
  const { access, userAccess, workspace, planInfo } = useAuthContext();
  const { isCollaborator = false, isOrgAdminOrOwner = false } = userAccess || {};
  const { isCustomPlan = false, isPlanCancelled = false } = planInfo || {};
  const showPlansLink = !isCustomPlan && isOrgAdminOrOwner && !isPlanCancelled;

  return (
    <div className='global-sidebar-content bg-white h-100 d-flex flex-column'>
      <div className='global-sidebar-content__header flex-item gap-2 space-between bb-1px-E3E8EF p-12-20px h-56px'>
        <Link to={PATHS.V2.HOME} onClick={onNavigate} className='d-flex'>
          <CustomIcon name='uxarmy_logo' width={100} height={30} />
        </Link>

        {onCollapse && (
          <Tooltip title='Collapse Sidebar' placement='right'>
            <Button
              type='text'
              size='small'
              className='global-sidebar-header-toggle'
              icon={<CustomIcon name='panel_left_close' width={20} height={20} />}
              onClick={onCollapse}
              data-attr={ANALYTICS_KEYS.GLOBAL_SIDEBAR.COLLAPSE_SIDEBAR}
            />
          </Tooltip>
        )}
      </div>

      <Flex vertical className='global-sidebar-content__main flex-1 overflow-hidden'>
        <WorkspaceName
          access={access}
          extraWidth={36}
          redirectToWorkspace
          className='p-16-20px'
          name={workspace?.name}
          extra={<WorkspaceSelection />}
        />

        <Divider className='m-0' />

        <SimpleBar className='st-8px flex-1 global-sidebar-content__scroll' style={{ minHeight: 0 }}>
          {!isCollaborator && <CreditsModal />}

          <div className='global-sidebar-primary-nav'>
            <Text className='p-10-20 pb-0 d-block fw-400 font-size-12px color-6C6C89'>Workspace</Text>
            <LinkItem icon='home' name='Home' link={PATHS.V2.HOME} onNavigate={onNavigate} />
            <LinkItem icon='projects' name='All Projects' link={PATHS.V2.PROJECTS.LIST} end={false} onNavigate={onNavigate} />
            {/* The listing of every study in the workspace. The picker below it is the create
                flow — the two used to share the name "All Studies", which left the workspace with
                no way to simply see its studies. */}
            <LinkItem icon='grid_1' name='All Studies' link={PATHS.V2.STUDIES.LIST} end={false} onNavigate={onNavigate} />

            {/* The same picker the project and All Studies pages open, rather than a list of
                product dashboards. Picking a type here used to land the user on that product's
                dashboard, leaving them to find its own Create button — so the sidebar's "Create
                Study" was the one entry point in the app that did not create a study.

                Hidden from collaborators, who cannot create one. Both pages that carry this
                picker already withhold it from them, and the sidebar was the way around that:
                every type in it leads to a creation screen a collaborator is not allowed to
                finish. */}
            {!isCollaborator && (
              <CreateStudyMenu placement='rightTop' variant='sidebar' onNavigate={onNavigate}>
                <Button
                  size='large'
                  className='w-100 d-flex p-10-20 h-bg-F7F7F8 transition'
                  type='text'
                  icon={<CustomIcon name='plus_circle' height={22} width={22} rootClassName='color-6C6C89' />}
                >
                  <Text className='fw-500' style={{ width: "calc(100% - 22px - 8px)" }}>
                    Create Study
                  </Text>
                  <CustomIcon name='chev_right' />
                </Button>
              </CreateStudyMenu>
            )}

            <Divider className='m-0' />
            <Text className='p-10-20 pb-0 d-block fw-400 font-size-12px color-6C6C89'>Research Ops</Text>
            <LinkItem
              icon='participant_hub'
              name={PARTICIPANT_HUB_NAME}
              link={PATHS.V2.PARTICIPANTS.LIST}
              onNavigate={onNavigate}
            />
            <LinkItem icon='tag_01' name='Tagging & Analysis' link={PATHS.V2.TAGS_LIST} onNavigate={onNavigate} />
          </div>
        </SimpleBar>
      </Flex>

      <div className='bt-1px-E3E8EF w-100 global-sidebar-content__footer'>
        {showPlansLink && (
          <LinkItem
            icon='diamond_01'
            link={PATHS.V2.PLANS}
            name={<div className='flex-item gap-1 space-between'>Explore Plans</div>}
            onNavigate={onNavigate}
          />
        )}

        <Popover
          arrow={false}
          trigger={["click"]}
          placement='right'
          styles={{ container: { padding: 5, width: "100%" } }}
          content={
            <div>
              <LinkItem
                key='tbt'
                icon='file_06'
                name='Usability Testing Templates'
                link='/templates?type=task#BY_COMMUNITY'
                onNavigate={onNavigate}
              />
              <Divider className='m-0' />
              <LinkItem
                key='surv'
                icon='file_06'
                name='Survey Templates'
                link='/templates?type=survey#BY_COMMUNITY'
                onNavigate={onNavigate}
              />
            </div>
          }
        >
          <Button
            size='large'
            className='w-100 d-flex p-10-20 h-bg-F7F7F8 transition'
            type='text'
            icon={<CustomIcon name='file_06' height={22} width={22} rootClassName='color-6C6C89' />}
          >
            <Text className='fw-500' style={{ width: "calc(100% - 22px - 8px)" }}>
              Templates
            </Text>
            <CustomIcon name='chev_right' />
          </Button>
        </Popover>

        <Popover
          arrow={false}
          trigger={["click"]}
          placement='right'
          styles={{ container: { padding: 5, width: "100%" } }}
          content={
            <div>
              <LinkItem
                key='help'
                icon='book_open_01'
                name='Help Center'
                link={PATHS.FAQ}
                target='_blank'
                isExternalLink
                onNavigate={onNavigate}
              />
              <Divider className='m-0' />
              <LinkItem
                key='recr'
                icon='image_user'
                name='Recruitment Support'
                link={PATHS.CONTACT_US}
                target='_blank'
                isExternalLink
                onNavigate={onNavigate}
              />
            </div>
          }
        >
          <Button
            size='large'
            className='w-100 d-flex p-10-20 h-bg-F7F7F8 transition'
            type='text'
            icon={<CustomIcon name='help_circle_1' height={22} width={22} rootClassName='color-6C6C89' />}
          >
            <Text className='fw-500' style={{ width: "calc(100% - 22px - 8px)" }}>
              Help & Resources
            </Text>
            <CustomIcon name='chev_right' />
          </Button>
        </Popover>

        <LinkItem
          name='Settings'
          icon='settings_02'
          link={isOrgAdminOrOwner ? PATHS.V2.ORGANIZATION : PATHS.V2.WORKSPACE}
          onNavigate={onNavigate}
        />

        <AccountDropdown />
      </div>
    </div>
  );
}

export default function GlobalSidebar(props) {
  const [minimized, setMinimized] = useState(() => localStorage.getItem(GLOBAL_SIDEBAR_MINIMIZED_KEY) === "1");

  const handleMinimize = () => {
    setMinimized(true);
    localStorage.setItem(GLOBAL_SIDEBAR_MINIMIZED_KEY, "1");
  };

  const handleExpand = () => {
    setMinimized(false);
    localStorage.removeItem(GLOBAL_SIDEBAR_MINIMIZED_KEY);
  };

  if (minimized) return <GlobalSidebarRail onExpand={handleExpand} showHideButton={false} showTopControls />;

  return (
    <Sider width={300} theme='light' className='br-1px-E3E8EF position-relative h-100'>
      <GlobalSidebarContent {...props} onCollapse={handleMinimize} />
    </Sider>
  );
}

export function GlobalSidebarRail({
  onExpand = () => {},
  onHide = () => {},
  showHideButton = true,
  showExpandButton = true,
  showTopControls = true,
}) {
  const { user, userAccess, planInfo, workspace } = useAuthContext();
  const { isCollaborator = false, isOrgAdminOrOwner = false } = userAccess || {};
  const { isCustomPlan = false, isPlanCancelled = false } = planInfo || {};
  const showPlansLink = !isCustomPlan && isOrgAdminOrOwner && !isPlanCancelled;

  return (
    <Sider width={56} theme='light' className='global-app-shell__rail bg-white br-1px-E3E8EF'>
      <div className='global-app-shell__rail-main'>
        {showTopControls && (
          <>
            {showHideButton && (
              <RailButton
                icon='menu_01'
                onClick={onHide}
                tooltip='Hide Sidebar'
                dataAttr={ANALYTICS_KEYS.GLOBAL_SIDEBAR.HIDE_SIDEBAR}
              />
            )}
            {showExpandButton && (
              <RailButton
                icon='panel_left_open'
                onClick={onExpand}
                tooltip='Expand Sidebar'
                dataAttr={ANALYTICS_KEYS.GLOBAL_SIDEBAR.EXPAND_SIDEBAR}
              />
            )}
          </>
        )}
        <RailWorkspaceButton workspace={workspace} />
        <RailDivider />
        <RailLink icon='home' link={PATHS.V2.HOME} tooltip='Home' />
        <RailLink icon='projects' link={PATHS.V2.PROJECTS.LIST} tooltip='All Projects' end={false} />
        <RailLink icon='grid_1' link={PATHS.V2.STUDIES.LIST} tooltip='All Studies' end={false} />
        {/* Same picker as the expanded sidebar's, so the rail is the same app with less of it
            showing — including being withheld from collaborators. The wrapping div gives the
            Dropdown a plain element to hold its ref against, which the Tooltip inside RailButton
            would otherwise take. */}
        {!isCollaborator && (
          <CreateStudyMenu placement='rightTop' variant='rail'>
            <div>
              <RailButton icon='plus_circle' tooltip='Create Study' />
            </div>
          </CreateStudyMenu>
        )}

        <RailLink icon='participant_hub' link={PATHS.V2.PARTICIPANTS.LIST} tooltip={PARTICIPANT_HUB_NAME} />
        <RailLink icon='tag_01' link={PATHS.V2.TAGS_LIST} tooltip='Tagging & Analysis' />
      </div>

      <div className='global-app-shell__rail-footer'>
        <RailDivider />

        {showPlansLink && <RailLink icon='diamond_01' link={PATHS.V2.PLANS} tooltip='Explore Plans' />}

        <RailPopover
          placement='rightBottom'
          content={
            <div>
              <LinkItem key='tbt' icon='file_06' name='Usability Testing Templates' link='/templates?type=task#BY_COMMUNITY' />
              <Divider className='m-0' />
              <LinkItem key='surv' icon='file_06' name='Survey Templates' link='/templates?type=survey#BY_COMMUNITY' />
            </div>
          }
        >
          <div>
            <RailButton icon='file_06' tooltip='Templates' />
          </div>
        </RailPopover>

        <RailPopover
          placement='rightBottom'
          content={
            <div>
              <LinkItem key='help' icon='book_open_01' name='Help Center' link={PATHS.FAQ} target='_blank' isExternalLink />
              <Divider className='m-0' />
              <LinkItem
                key='recr'
                icon='image_user'
                name='Recruitment Support'
                link={PATHS.CONTACT_US}
                target='_blank'
                isExternalLink
              />
            </div>
          }
        >
          <div>
            <RailButton icon='help_circle_1' tooltip='Help & Resources' />
          </div>
        </RailPopover>

        <RailLink icon='settings_02' link={isOrgAdminOrOwner ? PATHS.V2.ORGANIZATION : PATHS.V2.WORKSPACE} tooltip='Settings' />
        <RailDivider />
        <RailPopover
          placement='rightBottom'
          classNames={{ root: "account-dropdown-popover" }}
          styles={{ root: { width: 270 }, container: { padding: 0, width: 270 } }}
          content={
            <div className='account-dropdown__content'>
              <Text className='account-dropdown__email'>{user?.email}</Text>

              <ProfilePopoverLink key='profile' icon='user_edit' name='Edit Profile' link={PATHS.V2.EDIT_PROFILE} />
              <Divider className='account-dropdown__divider' />

              {showPlansLink && (
                <>
                  <ProfilePopoverLink key='upgrade' icon='upgrade' name='Upgrade Plan' link={PATHS.V2.PLANS} />
                  <Divider className='account-dropdown__divider' />
                </>
              )}

              <ProfilePopoverLink
                key='logout'
                icon='logout'
                name='Log Out'
                link={PATHS.LOGOUT}
                onClick={() => {
                  posthog.reset();
                  localStorage.removeItem("isPopOverOpen");
                }}
              />
            </div>
          }
        >
          <Tooltip title={user?.email || "Account"} placement='right'>
            <button type='button' className='global-app-shell__rail-profile-btn' aria-label='Open profile menu'>
              <UXAvatar userId={user?.id} logo={user?.profile_pic_name} text={user?.first_name?.charAt()} size={28} />
            </button>
          </Tooltip>
        </RailPopover>
      </div>
    </Sider>
  );
}

function RailDivider() {
  return <div className='global-app-shell__rail-divider' aria-hidden='true' />;
}

function RailWorkspaceButton({ workspace }) {
  const [first = "", last = ""] = (workspace?.name || "").split(" ");
  const avatarText = first ? first[0] + (last?.[0] || first?.[1] || "") : "";

  return (
    <Tooltip title={workspace?.name} placement='right'>
      <Link to={PATHS.V2.WORKSPACE} className='global-app-shell__rail-workspace-link' aria-label='Open workspace'>
        <div className='global-app-shell__rail-workspace-avatar'>
          <span className='fw-500 font-size-14px text-uppercase'>{avatarText}</span>
        </div>
      </Link>
    </Tooltip>
  );
}

function ProfilePopoverLink({ target = "_self", icon, name, link, onClick = () => {} }) {
  return (
    <Link to={link} target={target} onClick={onClick} className='account-dropdown__link transition'>
      <Flex gap={16} align='center' className='w-100'>
        <CustomIcon name={icon} height={24} width={24} rootClassName='account-dropdown__link-icon' />
        <Text className='account-dropdown__link-text'>{name}</Text>
      </Flex>
    </Link>
  );
}

function RailLink({ icon, link, tooltip = "", end = true }) {
  return (
    <Tooltip title={tooltip} placement='right'>
      <NavLink to={link} end={end} className={({ isActive }) => `global-app-shell__rail-link${isActive ? " active" : ""}`}>
        <CustomIcon name={icon} width={22} height={22} />
      </NavLink>
    </Tooltip>
  );
}

function RailPopover({ children, content, placement = "rightTop", ...props }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      arrow={false}
      trigger='click'
      placement={placement}
      align={{ offset: [8, 0] }}
      styles={{ container: { padding: 5, width: "100%" } }}
      {...props}
      open={open}
      onOpenChange={setOpen}
      content={<div onClick={() => setOpen(false)}>{content}</div>}
    >
      {children}
    </Popover>
  );
}

function RailButton({ icon, onClick, tooltip = "", dataAttr = "" }) {
  return (
    <Tooltip title={tooltip} placement='right'>
      <Button
        type='text'
        size='large'
        className='global-app-shell__rail-btn'
        icon={<CustomIcon name={icon} width={22} height={22} />}
        onClick={onClick}
        data-attr={dataAttr}
      />
    </Tooltip>
  );
}

function LinkItem({ target = "_self", icon, name, link, disabled = false, isExternalLink = false, end = true, onNavigate = () => {} }) {
  return (
    <NavLink
      to={link}
      end={end}
      target={target}
      onClick={onNavigate}
      className={({ isActive }) => `d-flex p-10-20 h-bg-F7F7F8 transition${isActive ? " active" : ""} ${disabled ? "disabled" : ""}`}
    >
      <Flex gap={8} align='center' className='w-100'>
        <CustomIcon name={icon} height={22} width={22} rootClassName='color-6C6C89' />
        <Text style={{ width: "calc(100% - 22px - 8px)" }}>{name}</Text>
        {isExternalLink && <CustomIcon name='arrow_narrow_up_right' height={22} width={22} rootClassName='color-6C6C89' />}
      </Flex>
    </NavLink>
  );
}
